import webstomp from "webstomp-client";
import { makeAutoObservable } from "mobx";
import { z } from "zod";
import { apiConfigUrl } from "./apiUrl";
import { onError } from "./onError";
import { rest } from "./rest";

const makeWSUrl = () =>
  `${window.location.origin.replace(/^http/, "ws")}/gateway-service/services/push-service/broadcast`;

const zObjectModelVersion = z.coerce.number();
const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

type WSClient = {
  connect: (
    headers: Record<string, string>,
    onConnect: () => void,
    onError: (error: unknown) => void,
  ) => void;
  disconnect?: () => void;
  subscribe: (
    destination: string,
    callback: (message: { body: unknown }) => void,
  ) => void;
  connected: boolean;
};

export const objectModelVersionStore = makeAutoObservable({
  version: null as number | null,
  initialized: false,
  client: null as WSClient | null,

  setVersion(version: number | null) {
    this.version = version;
  },

  setInitialized(value: boolean) {
    this.initialized = value;
  },

  setClient(client: WSClient | null) {
    this.client = client;
  },

  disconnect() {
    const { client } = this;
    if (client && client.connected) {
      client.disconnect?.();
    }
    this.setClient(null);
  },

  clear() {
    this.disconnect();
    this.setVersion(null);
    this.setInitialized(false);
  },

  async loadVersion() {
    const resp = await rest.get(apiConfigUrl("/objectmodelversion"));
    this.setVersion(zObjectModelVersion.parse(resp.data));
  },

  connect() {
    if (!this.initialized) {
      return;
    }

    this.disconnect();

    // Исключаем v10.stomp: в STOMP 1.0 нет heartbeat, и webstomp-client
    // не запускает pinger — сервер не получает heartbeat от клиента
    // и закрывает сессию через ~30с ("Session closed").
    const client = webstomp.client(makeWSUrl(), {
      debug: false,
      heartbeat: { incoming: 10000, outgoing: 10000 },
      protocols: ["v12.stomp", "v11.stomp"],
    }) as WSClient;
    this.setClient(client);

    client.connect(
      {},
      () => {
        client.subscribe("/objectmodelversion", (message) => {
          try {
            this.setVersion(zObjectModelVersion.parse(message.body));
          } catch (error) {
            onError(toError(error));
          }
        });
      },
      (error) => {
        if (!this.initialized) {
          return;
        }

        if (
          error instanceof CloseEvent &&
          (error.code === 1002 || error.code === 1006)
        ) {
          this.connect();
          return;
        }
        onError(toError(error));
      },
    );
  },

  async init() {
    if (this.initialized) {
      return;
    }

    this.setInitialized(true);

    try {
      await this.loadVersion();
    } catch (error) {
      onError(toError(error));
    }

    this.connect();
  },
});
